import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileSpreadsheet, Loader2, ReceiptText, RefreshCw, Shield } from 'lucide-react';
import { organizationAPI, saveBlob } from '../../services/api';
import { formatMoney } from '../../utils/formatMoney';
import CancelOrderModal from '../CancelOrderModal';
import OrgRecordDetailsModal from '../OrgRecordDetailsModal';
import { useToast } from '../shared/Toast';
import { LoadFailed } from '../settings/instantSettings';
import {
  Column, DANGER_GHOST_BTN, DataTable, DateRange, EmptyState, GHOST_BTN, ListPage, ListToolbar, StatusChip,
} from '../shared/listPage';
import { OrgStore, serverMessage } from './orgTypes';
import { TFn, docTypeLabel, fmtDateTime, orderTypeLabel, statusChip, storeLabel } from './orgFormat';
import { DocTypeFilter, OrgOrdersState, OrdersView } from './useOrgOrders';
import OrgConfirm from './OrgConfirm';
import { cancellationPrintWarning } from '../../utils/cancellationPrint';

/**
 * «Παραγγελίες & παραστατικά» των κεντρικών στη διάταξη «Πίνακας» (14/10/2026): κατάστημα, Παραγγελίες |
 * Παραστατικά, περίοδος, ομάδες παραστατικών με πλήθη, πίνακας και συρτάρι λεπτομερειών. Στα παραστατικά τα
 * ποσά είναι του ΠΑΡΑΣΤΑΤΙΚΟΥ (καθαρό/ΦΠΑ/σύνολο) και η «Εξαγωγή σε Excel» βγάζει .xlsx με τα ίδια φίλτρα
 * (24/10/2026). Οι ακυρώσεις από
 * τα κεντρικά (μόνο ιδιοκτήτης/διαχειριστής) είναι πλέον στο συρτάρι — ίδιοι κανόνες με το κουμπί της
 * παλιάς γραμμής: όχι σε αποτυχημένη κάρτα ή ακυρωμένη, παραστατικό μόνο με MARK. Ίδιες κλήσεις: η
 * παραγγελία με το κοινό CancelOrderModal (variant 'org': το force ΔΕΝ ακυρώνει το παραστατικό), το
 * παραστατικό με επιβεβαίωση και POST …/fiscal/:id/cancel.
 */

export interface OrgOrdersTabProps {
  orgId: number;
  /** organization.settings — για τη σημείωση του κεντρικού ελέγχου ακυρώσεων. */
  orgSettings: any;
  stores: OrgStore[];
  canManage: boolean;
  orders: OrgOrdersState;
}

const DOC_GROUPS: DocTypeFilter[] = ['all', 'receipts', 'invoices', 'credit', 'order_slips'];
const ROW_LIMIT = 500; // ίδιο με το LIMIT του server

/**
 * Πιστωτικά / ακυρωτικά (11.4, 5.1, 5.2 — η ομάδα «credit» του server). Ο server στέλνει is_credit· ο κωδικός
 * μένει εφεδρεία για απάντηση παλιού backend. Σημαδεύονται όπως στη σελίδα παραστατικών του καταστήματος:
 * κόκκινη ετικέτα τύπου, ποσό όπως εκδόθηκε (θετικό).
 */
const CREDIT_CODES = ['11.4', '5.1', '5.2'];
const isCreditDoc = (d: any): boolean => d?.is_credit === true || CREDIT_CODES.includes(String(d?.invoice_type_code || ''));

/** Ποσό του ΠΑΡΑΣΤΑΤΙΚΟΥ (όχι της παραγγελίας — σφάλμα 15/10/2026: έδειχνε το orders.total_amount). */
const docMoney = (v: unknown) => (v != null && v !== '' && Number.isFinite(Number(v))
  ? <span className="tabular-nums">{formatMoney(Number(v))}</span>
  : <span className="text-t4">—</span>);

/** Όνομα αρχείου από το Content-Disposition (αν το εκθέτει ο server), αλλιώς το δοσμένο. */
const fileNameOf = (disposition: unknown, fallback: string): string => {
  const m = /filename="?([^";]+)"?/i.exec(typeof disposition === 'string' ? disposition : '');
  return m ? m[1] : fallback;
};

const OrgOrdersTab: React.FC<OrgOrdersTabProps> = ({ orgId, orgSettings, stores, canManage, orders }) => {
  const { t: tRaw, i18n } = useTranslation(['orgDashboard', 'admin', 'common', 'listPage', 'orders']);
  const t = tRaw as unknown as TFn;
  const { showToast } = useToast();
  const {
    storeId, setStoreId, view, setView, from, to, setRange, rows, loading, error, busy, setBusy, showFailed, setShowFailed,
    phantomCount, docType, setDocType, counts, reload,
  } = orders;
  const [detail, setDetail] = useState<{ kind: 'fiscal' | 'order'; id: number } | null>(null);
  const [exporting, setExporting] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<any | null>(null);
  const [fiscalConfirm, setFiscalConfirm] = useState<number | null>(null);
  const cancelBoxRef = useRef<HTMLDivElement>(null);

  // Το παράθυρο ακύρωσης ανοίγει πάνω από το συρτάρι: το focus στο πεδίο της αιτίας (αλλιώς έμενε στο
  // κουμπί του συρταριού και το Tab γύριζε μόνο μέσα σε αυτό).
  useEffect(() => {
    if (cancelTarget) cancelBoxRef.current?.querySelector<HTMLTextAreaElement>('textarea')?.focus();
  }, [cancelTarget]);

  const restricted = orgSettings?.restrict_cancellations === true;

  /* ───────── ενέργειες (ίδιες κλήσεις με πριν) ───────── */

  const confirmHqCancel = async (reason: string, force?: boolean) => {
    if (!storeId || !cancelTarget) return;
    setBusy(cancelTarget.id);
    try {
      await organizationAPI.cancelStoreOrder(orgId, storeId, cancelTarget.id, reason, force === true);
      await reload();
      setDetail(null);
    } catch (e: any) {
      const d = e?.response?.data || {};
      if (d.requires_force && !force) {
        const err: any = new Error(d.message || '');
        err.requiresForce = true;
        err.fiscalCanCancel = false; // το force των κεντρικών δεν ακυρώνει ποτέ το παραστατικό
        throw err;
      }
      throw new Error(d.message || t('orgDashboard:orders.cancelFailed'));
    } finally {
      setBusy(null);
    }
  };

  const cancelFiscal = async () => {
    const documentId = fiscalConfirm;
    setFiscalConfirm(null);
    if (!storeId || documentId == null) return;
    setBusy(documentId);
    try {
      const result = await organizationAPI.cancelStoreFiscal(orgId, storeId, documentId);
      // Το ακυρωτικό τυπώνεται στο κατάστημα· αν δεν έφτασε στον εκτυπωτή του, τα κεντρικά το μαθαίνουν εδώ.
      const printWarning = result?.success ? cancellationPrintWarning(result, t) : null;
      if (printWarning) showToast('warning', printWarning);
      await reload();
      setDetail(null);
    } catch (e) {
      showToast('error', serverMessage(e, t('orgDashboard:orders.cancelFailed')));
    } finally {
      setBusy(null);
    }
  };

  /**
   * «Εξαγωγή σε Excel»: τα ΙΔΙΑ φίλτρα με τον πίνακα (κατάστημα, περίοδος, ομάδα τύπου) και ΟΛΕΣ οι γραμμές —
   * ο server φτιάχνει το .xlsx (όχι CSV) και αρνείται πάνω από 50.000 με το πλήθος.
   */
  const exportExcel = async () => {
    if (!storeId || exporting) return;
    setExporting(true);
    try {
      const resp = await organizationAPI.exportStoreFiscal(orgId, storeId, from, to, docType);
      const blob = resp.data instanceof Blob
        ? resp.data
        : new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveBlob(blob, fileNameOf(resp.headers?.['content-disposition'], `parastatika_kat${storeId}_${from}_${to}.xlsx`));
    } catch (err: any) {
      // responseType 'blob': και το σώμα του σφάλματος είναι Blob — διαβάζουμε το JSON του server.
      let data: any = null;
      try {
        const text = await err?.response?.data?.text?.();
        if (text) data = JSON.parse(text);
      } catch { /* μένει το γενικό μήνυμα */ }
      const nf = new Intl.NumberFormat(i18n?.language === 'en' ? 'en-GB' : 'el-GR');
      let msg = t('orgDashboard:orders.export.failed');
      if (data?.code === 'too_many_documents') {
        msg = t('orgDashboard:orders.export.tooMany', { total: nf.format(Number(data.count) || 0), limit: nf.format(Number(data.limit) || 0) });
      } else if (data?.code === 'no_documents') {
        msg = t('orgDashboard:orders.export.empty');
      } else if (err?.response?.status === 403) {
        msg = t('orgDashboard:orders.forbidden');
      }
      showToast('error', msg);
    } finally {
      setExporting(false);
    }
  };

  /* ───────── στήλες ───────── */

  const orderColumns = useMemo<Column<any>[]>(() => [
    {
      key: 'code',
      header: t('orgDashboard:orders.colCode'),
      width: '110px',
      stack: 'title',
      render: o => <span className="font-mono text-t2">{o.short_code || o.id}</span>,
      stackRender: o => t('orgDashboard:orders.orderShort', { code: o.short_code || o.id }),
    },
    {
      key: 'date',
      header: t('common:date'),
      width: 'minmax(120px, 1fr)',
      render: o => <span className="tabular-nums text-t2">{fmtDateTime(o.order_timestamp)}</span>,
      stackRender: o => fmtDateTime(o.order_timestamp),
    },
    {
      key: 'type',
      header: t('orgDashboard:orders.colType'),
      width: 'minmax(100px, 1fr)',
      render: o => <span className="text-t2">{orderTypeLabel(t, o.order_type)}</span>,
      stackRender: o => orderTypeLabel(t, o.order_type),
    },
    {
      key: 'customer',
      header: t('common:customer'),
      width: 'minmax(140px, 1.4fr)',
      hideBelow: 'medium',
      render: o => (o.customer_name ? <span className="truncate block">{o.customer_name}</span> : <span className="text-t4">—</span>),
    },
    {
      key: 'amount',
      header: t('orgDashboard:orders.colAmount'),
      width: '110px',
      align: 'right',
      stack: 'end',
      render: o => <span className="font-medium tabular-nums">{formatMoney(Number(o.total_amount) || 0)}</span>,
    },
    {
      key: 'fiscal',
      header: t('orgDashboard:orders.colFiscal'),
      width: '120px',
      hideBelow: 'medium',
      render: o => (o.has_fiscal
        ? <span className="text-t2" title={t('orgDashboard:orders.hasFiscalTitle')}>{t('orgDashboard:orders.hasFiscal')}</span>
        : <span className="text-t4" title={t('orgDashboard:orders.noFiscalTitle')}>—</span>),
    },
    {
      key: 'status',
      header: t('common:status'),
      width: '150px',
      render: o => {
        if (o.phantom) {
          return <StatusChip tone="danger" title={t('orgDashboard:orders.phantomTitle')}>{t('orgDashboard:orders.phantom')}</StatusChip>;
        }
        const c = statusChip(t, o.status);
        return <StatusChip tone={c.tone} title={o.status === 'cancelled' && o.cancel_reason ? o.cancel_reason : undefined}>{c.label}</StatusChip>;
      },
    },
  ], [t]);

  const fiscalColumns = useMemo<Column<any>[]>(() => [
    {
      key: 'number',
      header: t('orgDashboard:orders.colDocument'),
      width: 'minmax(110px, 1fr)',
      stack: 'title',
      render: d => <span className="font-mono text-t2">{d.series ? `${d.series}/${d.document_number}` : (d.document_number || d.id)}</span>,
      stackRender: d => `${docTypeLabel(t, d.invoice_type_code)} ${d.series ? `${d.series}/${d.document_number}` : (d.document_number || d.id)}`,
    },
    {
      key: 'type',
      header: t('orgDashboard:orders.colType'),
      width: 'minmax(150px, 1.4fr)',
      hideBelow: 'narrow',
      render: d => (isCreditDoc(d)
        ? <StatusChip tone="danger" title={d.invoice_type_code || ''}>{docTypeLabel(t, d.invoice_type_code)}</StatusChip>
        : <span className="text-t2" title={d.invoice_type_code || ''}>{docTypeLabel(t, d.invoice_type_code)}</span>),
    },
    {
      key: 'customer',
      header: t('common:customer'),
      width: 'minmax(140px, 1.4fr)',
      hideBelow: 'medium',
      render: d => (
        <>
          <span className="block truncate" title={d.customer_name || ''}>{d.customer_name || <span className="text-t4">—</span>}</span>
          {d.customer_vat_number && <span className="block text-xs text-t3">{t('orgDashboard:orders.vat', { vat: d.customer_vat_number })}</span>}
        </>
      ),
    },
    {
      key: 'mark',
      header: 'MARK',
      width: 'minmax(120px, 1fr)',
      hideBelow: 'medium',
      render: d => (d.mark ? <span className="font-mono text-[11px] text-t3 truncate block" title={d.mark}>{d.mark}</span> : <span className="text-t4">—</span>),
    },
    {
      key: 'date',
      header: t('common:date'),
      width: 'minmax(110px, 1fr)',
      render: d => <span className="tabular-nums text-t2">{fmtDateTime(d.created_at)}</span>,
      stackRender: d => fmtDateTime(d.created_at),
    },
    {
      key: 'net',
      header: t('orgDashboard:orders.colNet'),
      width: '96px',
      align: 'right',
      hideBelow: 'medium',
      render: d => <span className="text-t2">{docMoney(d.total_net_amount)}</span>,
    },
    {
      key: 'vat',
      header: t('orgDashboard:orders.colVat'),
      width: '88px',
      align: 'right',
      hideBelow: 'medium',
      render: d => <span className="text-t2">{docMoney(d.total_vat_amount)}</span>,
    },
    {
      key: 'amount',
      header: t('orgDashboard:orders.colTotal'),
      width: '110px',
      align: 'right',
      stack: 'end',
      // Το σύνολο του ΠΑΡΑΣΤΑΤΙΚΟΥ (total_gross_amount). Ως 24/10/2026 εδώ έβγαινε το σύνολο της παραγγελίας
      // (σφάλμα 15/10): λάθος σε πιστωτικά, τιμολόγια, προκαταβολές και «—» σε κάθε παραστατικό τραπεζιού.
      render: d => <span className="font-medium">{docMoney(d.total_gross_amount)}</span>,
      stackRender: d => (d.total_gross_amount != null ? formatMoney(Number(d.total_gross_amount)) : '—'),
    },
    {
      key: 'status',
      header: t('common:status'),
      width: '130px',
      render: d => {
        const c = statusChip(t, d.status);
        return <StatusChip tone={c.tone}>{c.label}</StatusChip>;
      },
    },
  ], [t]);

  /* ───────── συρτάρι: ποια ενέργεια επιτρέπεται (ίδιοι κανόνες με την παλιά γραμμή) ───────── */

  const listRow = detail && ((detail.kind === 'order' && view === 'orders') || (detail.kind === 'fiscal' && view === 'fiscal'))
    ? rows.find(r => r.id === detail.id) || null
    : null;

  let footerStart: React.ReactNode = null;
  if (canManage && listRow && detail) {
    if (detail.kind === 'order') {
      if (!listRow.phantom && listRow.status !== 'cancelled') {
        footerStart = (
          <button type="button" className={DANGER_GHOST_BTN} disabled={busy === listRow.id} onClick={() => setCancelTarget(listRow)} data-testid="org-order-cancel">
            {busy === listRow.id && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
            {t('orgDashboard:orders.cancelOrder')}
          </button>
        );
      }
    } else if (listRow.status !== 'cancelled') {
      footerStart = listRow.mark ? (
        <button type="button" className={DANGER_GHOST_BTN} disabled={busy === listRow.id} onClick={() => setFiscalConfirm(listRow.id)} data-testid="org-fiscal-cancel">
          {busy === listRow.id && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
          {t('orgDashboard:orders.cancelFiscal')}
        </button>
      ) : (
        <span className="text-xs text-t3" data-testid="org-fiscal-nomark">{t('orgDashboard:orders.noMark')}</span>
      );
    }
  }

  // Esc/φόντο: πρώτα το παράθυρο που είναι από πάνω, μετά το συρτάρι.
  const closeDetail = () => {
    if (cancelTarget) { setCancelTarget(null); return; }
    if (fiscalConfirm != null) { setFiscalConfirm(null); return; }
    setDetail(null);
  };

  const storeSelect = (
    <select
      className="lp-select"
      aria-label={t('orgDashboard:orders.store')}
      title={t('orgDashboard:orders.store')}
      value={storeId ?? ''}
      onChange={e => setStoreId(e.target.value ? parseInt(e.target.value, 10) : null)}
      data-testid="org-orders-store"
    >
      {stores.map(s => <option key={s.id} value={s.id}>{storeLabel(s)} (#{s.id})</option>)}
    </select>
  );

  const viewSeg = (
    <div className="lp-seg" role="radiogroup" aria-label={t('orgDashboard:orders.viewLabel')}>
      {(['orders', 'fiscal'] as OrdersView[]).map(v => (
        <button key={v} type="button" role="radio" aria-checked={view === v} onClick={() => setView(v)} data-testid={`org-orders-view-${v}`}>
          {v === 'orders' ? t('orgDashboard:orders.viewOrders') : t('orgDashboard:orders.viewFiscal')}
        </button>
      ))}
    </div>
  );

  const docSeg = view === 'fiscal' ? (
    <div className="lp-seg org-docseg" role="radiogroup" aria-label={t('orgDashboard:orders.docTypeLabel')} data-testid="org-doc-types">
      {DOC_GROUPS.map(key => {
        const n = counts[key];
        // Ομάδες χωρίς παραστατικό στο διάστημα δεν εμφανίζονται (το «Όλα» πάντα μένει)· η ΕΠΙΛΕΓΜΕΝΗ μένει
        // ορατή ακόμη κι αν είναι 0, ώστε να φαίνεται γιατί ο πίνακας είναι άδειος.
        if (key !== 'all' && !n && docType !== key) return null;
        return (
          <button key={key} type="button" role="radio" aria-checked={docType === key} onClick={() => setDocType(key)} data-testid={`org-doc-${key}`}>
            {t(`orgDashboard:orders.docGroups.${key}`)}
            {n != null && <span className="org-seg-count">{n}</span>}
          </button>
        );
      })}
    </div>
  ) : null;

  const noun = view === 'orders' ? 'orders' : 'documents';
  const summary = error ? null : (
    <>
      {t(`orgDashboard:orders.count.${noun}`, { count: rows.length })}
      {rows.length >= ROW_LIMIT && <> · {t('orgDashboard:orders.limitNote', { count: ROW_LIMIT })}</>}
    </>
  );

  const columns = view === 'orders' ? orderColumns : fiscalColumns;

  return (
    <ListPage
      eyebrow={t('orgDashboard:nav.groups.overview')}
      title={t('orgDashboard:orders.title')}
      subtitle={t('orgDashboard:orders.subtitle')}
      testId="org-orders"
    >
      <ListToolbar
        filters={(
          <>
            {storeSelect}
            {viewSeg}
            <DateRange from={from} to={to} onChange={r => setRange(r.from, r.to)} allowAll={false} testId="org-orders-range" />
          </>
        )}
        actions={(
          <>
            {view === 'fiscal' && (
              <button
                type="button"
                className={GHOST_BTN}
                onClick={exportExcel}
                // Άδειος πίνακας = άδειο φίλτρο: τίποτα για εξαγωγή.
                disabled={exporting || loading || !!error || rows.length === 0 || !storeId}
                aria-busy={exporting || undefined}
                title={t('orgDashboard:orders.export.hint')}
                data-testid="org-fiscal-export"
              >
                {exporting
                  ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                  : <FileSpreadsheet className="w-4 h-4" aria-hidden="true" />}
                {exporting ? t('orgDashboard:orders.export.working') : t('orgDashboard:orders.export.button')}
              </button>
            )}
            <button type="button" className={GHOST_BTN} onClick={() => reload()} disabled={loading} data-testid="org-orders-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
              {t('common:refresh')}
            </button>
          </>
        )}
        summary={loading && rows.length === 0 ? null : summary}
      />

      {docSeg && <div className="org-below-toolbar">{docSeg}</div>}

      <div className="org-notes">
        {/* Ο κεντρικός έλεγχος ακυρώσεων (διακόπτης οργανισμού· προεπιλογή ΚΛΕΙΣΤΟΣ) */}
        <p className="org-note" data-testid="org-cancel-control">
          <Shield aria-hidden="true" />
          <span>{restricted ? t('orgDashboard:orders.restrictedOn') : t('orgDashboard:orders.restrictedOff')}</span>
        </p>
        {view === 'orders' && phantomCount > 0 && (
          <p className="org-note" data-testid="org-phantoms">
            <span className="org-note-tight">
              {showFailed
                ? t('orgDashboard:orders.phantomShown', { count: phantomCount })
                : t('orgDashboard:orders.phantomHidden', { count: phantomCount })}
            </span>
            <button type="button" className="xs-link" onClick={() => setShowFailed(v => !v)} data-testid="org-phantoms-toggle">
              {showFailed ? t('orgDashboard:orders.hide') : t('orgDashboard:orders.show')}
            </button>
          </p>
        )}
      </div>

      {error ? (
        <LoadFailed
          message={error === 'forbidden' ? t('orgDashboard:orders.forbidden') : t(`orgDashboard:orders.loadFailed.${noun}`)}
          onRetry={() => reload()}
        />
      ) : (
        <DataTable
          key={view}
          columns={columns}
          rows={rows}
          rowKey={r => r.id}
          onRowClick={r => setDetail({ kind: view === 'orders' ? 'order' : 'fiscal', id: r.id })}
          selectedKey={detail?.id ?? null}
          loading={loading}
          stickyHeader
          rowClassName={r => (r.phantom ? 'opacity-60' : undefined)}
          rowLabel={r => (view === 'orders'
            ? t('orgDashboard:orders.rowOrder', { code: r.short_code || r.id, when: fmtDateTime(r.order_timestamp) })
            : t('orgDashboard:orders.rowDocument', { type: docTypeLabel(t, r.invoice_type_code), when: fmtDateTime(r.created_at) }))}
          empty={(
            <EmptyState
              icon={ReceiptText}
              title={stores.length === 0 ? t('admin:noStoresInOrg') : t(`orgDashboard:orders.empty.${noun}`)}
              hint={stores.length === 0 ? undefined : t('orgDashboard:orders.emptyHint')}
            />
          )}
          testId="org-orders-table"
        />
      )}

      {detail && storeId != null && (
        <OrgRecordDetailsModal
          orgId={orgId}
          storeId={storeId}
          kind={detail.kind}
          recordId={detail.id}
          onClose={closeDetail}
          onOpenFiscal={documentId => setDetail({ kind: 'fiscal', id: documentId })}
          footerStart={footerStart}
        />
      )}

      {/* Ακύρωση παραγγελίας από τα κεντρικά — κοινό παράθυρο, org variant. Πάνω από το συρτάρι (z 80 > 70)·
          νέο κάθε φορά (key), ώστε ένα «έχει παραστατικό» να μη μείνει για την επόμενη παραγγελία. */}
      <div ref={cancelBoxRef} className="relative z-[80]">
        <CancelOrderModal
          key={cancelTarget ? `cancel-${cancelTarget.id}` : 'cancel-none'}
          isOpen={!!cancelTarget}
          orderCode={cancelTarget?.short_code || String(cancelTarget?.id || '')}
          variant="org"
          platformName={cancelTarget?.platform_name}
          onClose={() => setCancelTarget(null)}
          onConfirm={confirmHqCancel}
        />
      </div>

      <OrgConfirm
        isOpen={fiscalConfirm != null}
        title={t('orgDashboard:orders.cancelFiscalTitle')}
        message={t('orgDashboard:orders.cancelFiscalConfirm')}
        confirmLabel={t('orgDashboard:orders.cancelFiscalAction')}
        cancelLabel={t('orgDashboard:actions.cancel')}
        variant="danger"
        onConfirm={cancelFiscal}
        onCancel={() => setFiscalConfirm(null)}
      />
    </ListPage>
  );
};

export default OrgOrdersTab;
